import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {groupThousands, MAX_NUMBER_TO_WORDS_DIGITS, numberToWords} from '../utils/number-to-words';

// PRICE_WITH_TEXT (см. dataTypes в admin-front/tiny-editor.component.ts):
// пользователь вводит только цифры, компонент сам группирует их пробелами
// по разрядам и показывает письменную форму в скобках рядом — например,
// для уставного капитала "20 000" -> "20 000 (двадцать тысяч)". Наружу
// (onPriceChange) отдаётся уже собранная строка "число (пропись)" — именно
// она попадает в fieldsValue[field.code] и дальше вставляется в текст
// документа вместо {{code}}, как есть, без отдельного форматирования на
// стороне form.component.ts (см. onDateChange/onTimeChange для сравнения).
@Component({
  selector: 'app-price-input',
  templateUrl: './price-input.component.html',
  styleUrls: ['./price-input.component.css']
})
export class PriceInputComponent implements OnInit, OnChanges {

  @Input() model = '';
  @Input() field: any = {};
  @Input() currentLanguage = 'ru';
  @Input() disabled: boolean;
  @Input() placeholder: string;
  @Input() label: string = '';
  @Input() required: boolean = false;
  @Output() onPriceChange = new EventEmitter();

  // Инпут занимает по ширине только сами цифры (см. [attr.size] в .html —
  // нужно, чтобы пропись шла сразу после них, без зазора), а не весь
  // контейнер поля — без этого клик мимо цифр (по остальной части рамки
  // поля, где раньше был растянутый на всю ширину <input>) никуда не попадал.
  @ViewChild('priceInput') priceInputRef?: ElementRef<HTMLInputElement>;

  public digits = '';
  public formattedNumber = '';
  public words = '';
  public isFocused: boolean = false;

  ngOnInit() {
    if (this.required === undefined && this.field && this.field.mandatory !== undefined) {
      this.required = this.field.mandatory;
    }
    this.updateFromModel(this.model);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.model && !changes.model.firstChange) {
      this.updateFromModel(changes.model.currentValue);
    }
  }

  get hasValue(): boolean {
    return !!this.digits;
  }

  // Модель может прийти уже как готовая строка "20 000 (двадцать тысяч)"
  // (edit-режим существующего документа) — вытаскиваем из неё только цифры,
  // остальное компонент пересчитает сам.
  updateFromModel(value: string) {
    const digitsOnly = (value || '').toString().replace(/\D/g, '');
    this.digits = digitsOnly.slice(0, MAX_NUMBER_TO_WORDS_DIGITS);
    this.recalculate();
  }

  recalculate() {
    this.formattedNumber = groupThousands(this.digits);
    this.words = this.digits ? numberToWords(parseInt(this.digits, 10), this.currentLanguage) : '';
  }

  onFocus() {
    this.isFocused = true;
  }

  // Клик в любом месте рамки поля (не только по самим цифрам — см.
  // priceInputRef выше) должен ставить курсор в конец введённых цифр, как в
  // обычном инпуте на всю ширину.
  focusInput(event?: MouseEvent): void {
    if (this.disabled || !this.priceInputRef) {
      return;
    }
    if (event && event.target === this.priceInputRef.nativeElement) {
      return;
    }
    this.priceInputRef.nativeElement.focus();
  }

  onBlur() {
    this.isFocused = false;
  }

  onInput(event: any) {
    // Лишние цифры сверх MAX_NUMBER_TO_WORDS_DIGITS (дальше пропись ломается)
    // просто не принимаем — ввод/вставка обрезается до лимита.
    const digitsOnly = (event.target.value || '').replace(/\D/g, '');
    this.digits = digitsOnly.slice(0, MAX_NUMBER_TO_WORDS_DIGITS);
    this.recalculate();
    event.target.value = this.formattedNumber;
    this.emitChange();
  }

  emitChange() {
    const val = this.digits ? `${this.formattedNumber} (${this.words})` : '';
    this.onPriceChange.emit({val, digits: this.digits});
  }
}
